// src/components/accueil/FilActivite.tsx — SCE Issues
// Fil « Activité récente » de l'accueil V2 (Lot 9).
//
// Coupé en deux par la visite précédente de l'accueil :
//   Nouveau depuis votre dernière visite — ce qui s'est passé depuis
//   Déjà vu                              — le reste, pour le contexte
// Au premier passage (aucune visite connue), pas de coupure.
//
// « Par machine » (02/10) : un titre par N° de série, les événements d'un même
// problème ensemble ; le nouveau est marqué d'un point jaune au lieu d'une coupure.
//
// Un défaut sur plusieurs machines (19/10, piste A) : les événements identiques
// des fiches sœurs d'un groupe tiennent sur une ligne, une étiquette par machine
// avec son N° de problème (`regrouperFil`). Pas en mode « Par machine » : chaque
// machine y a son titre, c'est le but du mode.

import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { PastilleEvenement } from '@/components/issues/PastilleEvenement'
import { METIERS_COULEURS } from '@/config/metiers.config'
import { lienProbleme } from '@/utils/liensFiches'
import { pluriel, TitreGroupeMachine } from './GroupeMachine'
import {
  activiteParMachine,
  extraitDuGroupe,
  extraitEvenement,
  libelleAge,
  regrouperFil,
  verbeEvenement,
  verbeGroupe,
  type EvenementAccueil,
  type LigneFil,
} from '@/utils/accueil'

export function FilActivite({
  evenements,
  visitePrecedente,
  moi,
  maintenant,
  parMachine = false,
  repliees,
  onBasculer,
  limite,
}: {
  evenements: readonly EvenementAccueil[]
  /** Nombre de lignes au plus (une ligne de groupe compte pour une). */
  limite?: number
  visitePrecedente: string | null
  /** Nom affiché de l'utilisateur, pour écrire « Vous ». */
  moi: string
  maintenant: number
  /** Un titre par N° de série. */
  parMachine?: boolean
  repliees?: ReadonlySet<string>
  onBasculer?: (cle: string) => void
}): JSX.Element {
  if (parMachine) {
    return (
      <>
        {activiteParMachine(evenements.slice(0, limite)).map((g) => {
          const replie = repliees?.has(g.cle) ?? false
          const nbProblemes = new Set(g.items.map((e) => e.issue.id)).size
          return (
            <div key={g.cle}>
              <TitreGroupeMachine
                cle={g.cle}
                pn={g.pn}
                detail={`${pluriel(nbProblemes, 'problème')} · ${pluriel(g.items.length, 'événement')}`}
                replie={replie}
                nouveau={!!visitePrecedente && g.items.some((e) => e.nouveau)}
                onBascule={() => onBasculer?.(g.cle)}
              />
              {!replie &&
                g.items.map((e) => (
                  <LigneActivite
                    key={e.evenement.id}
                    e={e}
                    moi={moi}
                    maintenant={maintenant}
                    marqueNouveau={!!visitePrecedente && e.nouveau}
                  />
                ))}
            </div>
          )
        })}
      </>
    )
  }

  // Un défaut sur plusieurs machines (19/10) : une ligne pour les événements identiques du groupe.
  const lignes = regrouperFil(evenements).slice(0, limite)
  const rendre = (l: LigneFil): JSX.Element => (
    <LigneFilActivite key={l.principal.evenement.id} ligne={l} moi={moi} maintenant={maintenant} />
  )

  if (!visitePrecedente) return <>{lignes.map(rendre)}</>

  const nouvelles = lignes.filter((l) => l.nouveau)
  const vues = lignes.filter((l) => !l.nouveau)
  return (
    <>
      <Separateur nouveau>
        {nouvelles.length > 0
          ? `Nouveau depuis votre dernière visite · ${quand(visitePrecedente)}`
          : `Rien de nouveau depuis votre dernière visite · ${quand(visitePrecedente)}`}
      </Separateur>
      {nouvelles.map(rendre)}
      {vues.length > 0 && <Separateur>Déjà vu</Separateur>}
      {vues.map(rendre)}
    </>
  )
}

/** Une ligne du fil : un événement, ou le même événement sur plusieurs machines d'un groupe. */
export function LigneFilActivite({
  ligne,
  moi,
  maintenant,
}: {
  ligne: LigneFil
  moi: string
  maintenant: number
}): JSX.Element {
  if (ligne.membres.length < 2) return <LigneActivite e={ligne.principal} moi={moi} maintenant={maintenant} />
  return <LigneActiviteGroupe ligne={ligne} moi={moi} maintenant={maintenant} />
}

/**
 * Le même événement sur N machines : « a déclaré un défaut sur 2 machines »,
 * puis une étiquette par machine avec SON N° de problème (les fiches peuvent
 * diverger ensuite) ; l'étiquette ouvre sa fiche, le reste de la ligne la
 * première du groupe (sa carte « Même défaut sur… » mène aux autres).
 */
function LigneActiviteGroupe({
  ligne,
  moi,
  maintenant,
}: {
  ligne: LigneFil
  moi: string
  maintenant: number
}): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()
  const { evenement, issue } = ligne.principal
  const premier = ligne.membres[0]!
  const estMoi = !!moi && evenement.auteur.trim().toLowerCase() === moi.trim().toLowerCase()
  const extrait = extraitDuGroupe(evenement, issue)

  return (
    <div
      className="ligne-activite"
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'start',
        padding: '10px 18px',
        minHeight: 60,
        background: 'var(--color-bg-card)',
        color: 'var(--color-text-primary)',
      }}
    >
      <PastilleEvenement type={evenement.type} contenu={evenement.contenu} auteur={evenement.auteur} upn={evenement.auteurEmail} />
      <span className="flex flex-col min-w-0" style={{ gap: 3 }}>
        <button
          type="button"
          // Le bouton couvre toute la ligne (::after) ; les étiquettes passent au-dessus.
          className="ligne-activite-principal text-left"
          onClick={() => ouvrirFiche(lienProbleme(premier.issue.id), undefined, premier.evenement.id)}
          style={{ border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', color: 'inherit', cursor: 'pointer' }}
        >
          <span style={{ fontSize: 14, lineHeight: '19px' }}>
            <span style={{ fontWeight: 500 }}>{estMoi ? 'Vous' : evenement.auteur || 'Quelqu\'un'}</span>{' '}
            {verbeGroupe(evenement, estMoi, ligne.membres.length)}
          </span>
          {extrait && (
            <span
              style={{
                display: '-webkit-box',
                marginTop: 3,
                fontSize: 13,
                lineHeight: '18px',
                color: 'var(--color-gray-80)',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {extrait}
            </span>
          )}
        </button>
        <span className="flex flex-wrap" style={{ gap: 4, marginTop: 2 }}>
          {ligne.membres.map((m) => (
            <button
              key={m.evenement.id}
              type="button"
              className="ligne-activite-machine inline-flex items-center"
              onClick={() => ouvrirFiche(lienProbleme(m.issue.id), undefined, m.evenement.id)}
              title={`Ouvrir ${m.issue.id}${m.issue.numeroSerie ? ` (${m.issue.numeroSerie})` : ''}`}
              style={{
                gap: 6,
                padding: '2px 8px',
                minHeight: 26,
                borderRadius: 6,
                border: '1px solid var(--color-gray-20)',
                backgroundColor: 'var(--color-gray-5)',
                fontFamily: 'inherit',
                fontSize: 12,
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontWeight: 500 }}>{m.issue.numeroSerie || 'Sans équipement'}</span>
              <span className="inline-flex items-center" style={{ gap: 4, color: 'var(--color-text-secondary)' }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: METIERS_COULEURS[m.issue.metier] }} />
                {m.issue.id}
              </span>
            </button>
          ))}
        </span>
      </span>
      <span
        className="inline-flex items-center"
        style={{ gap: 6, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', paddingTop: 2 }}
      >
        {libelleAge(evenement.date, maintenant)}
      </span>
    </div>
  )
}

/** « hier 17:42 », « lun. 09:12 », « 12/09 08:30 ». */
function quand(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const auj = new Date()
  const jours = Math.round(
    (new Date(auj.getFullYear(), auj.getMonth(), auj.getDate()).getTime() -
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000,
  )
  if (jours === 0) return `aujourd'hui ${heure}`
  if (jours === 1) return `hier ${heure}`
  if (jours < 7) return `${d.toLocaleDateString('fr-FR', { weekday: 'short' })} ${heure}`
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${heure}`
}

function Separateur({ children, nouveau }: { children: string; nouveau?: boolean }): JSX.Element {
  return (
    <div
      className="flex items-center gap-2"
      style={{
        padding: nouveau ? '10px 18px 4px' : '14px 18px 4px',
        fontSize: 12,
        fontWeight: nouveau ? 500 : 400,
        color: nouveau ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        borderTop: nouveau ? undefined : '1px solid var(--color-gray-10)',
      }}
    >
      {nouveau && (
        <span
          aria-hidden="true"
          style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: 'var(--color-yellow)' }}
        />
      )}
      {children}
    </div>
  )
}

export function LigneActivite({
  e,
  moi,
  maintenant,
  marqueNouveau = false,
}: {
  e: EvenementAccueil
  moi: string
  maintenant: number
  /** Point jaune « nouveau depuis votre dernière visite » (quand il n'y a pas de coupure). */
  marqueNouveau?: boolean
}): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()
  const { evenement, issue } = e
  const estMoi = !!moi && evenement.auteur.trim().toLowerCase() === moi.trim().toLowerCase()
  const extrait = extraitEvenement(evenement, issue)

  return (
    <button
      type="button"
      // L'événement suit : la fiche marque ce qu'il a modifié.
      onClick={() => ouvrirFiche(lienProbleme(issue.id), undefined, evenement.id)}
      className="w-full text-left"
      style={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'start',
        padding: '10px 18px',
        minHeight: 60,
        border: 'none',
        background: 'var(--color-bg-card)',
        fontFamily: 'inherit',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
      }}
    >
      <PastilleEvenement type={evenement.type} contenu={evenement.contenu} auteur={evenement.auteur} upn={evenement.auteurEmail} />
      <span className="flex flex-col min-w-0" style={{ gap: 3 }}>
        <span style={{ fontSize: 14, lineHeight: '19px' }}>
          <span style={{ fontWeight: 500 }}>{estMoi ? 'Vous' : evenement.auteur || 'Quelqu\'un'}</span>{' '}
          {verbeEvenement(evenement, estMoi)}{' '}
          <span className="inline-flex items-center" style={{ gap: 5, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: METIERS_COULEURS[issue.metier] }} />
            {issue.id}
          </span>
        </span>
        {extrait && (
          <span
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--color-gray-80)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {extrait}
          </span>
        )}
      </span>
      <span
        className="inline-flex items-center"
        style={{ gap: 6, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', paddingTop: 2 }}
      >
        {marqueNouveau && (
          <span
            title="Nouveau depuis votre dernière visite"
            style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: 'var(--color-yellow)', border: '1px solid var(--color-black)' }}
          />
        )}
        {libelleAge(evenement.date, maintenant)}
      </span>
    </button>
  )
}
